/**
 * One-shot seeds that let another admin surface open BrandEditor's Create or
 * Edit modal pre-filled with values from an external catalogue, and have the
 * save stamp where those values came from.
 *
 * A seed splits into two halves at the modal:
 *  - FORM-BACKED keys are poured into the modal's inputs. The reviewer sees
 *    them, can change them, and the form's own value is what gets written.
 *  - CARRIED keys have no input in that modal (geolocation, founder, …). They
 *    ride along on the seed and are written verbatim on save; the modal lists
 *    them so nothing is written unseen.
 *
 * Everything here is pure so the write payload can be tested without mounting
 * the editor.
 */
import type { WatchBrand } from '../../types';

export type BrandExternalRefs = NonNullable<WatchBrand['externalRefs']>;
export type BrandFieldSources = NonNullable<WatchBrand['fieldSources']>;

interface BrandSeedBase {
  /** Only the fields the reviewer ticked. */
  values: Partial<WatchBrand>;
  /** Which external record this came from; merged over any existing refs. */
  externalRefs: BrandExternalRefs;
  /**
   * Provenance per written field, keyed by WatchBrand field path
   * (`country`, `founder.name`). Merged over any existing entries.
   */
  fieldSources: BrandFieldSources;
  /** Human name of the source, for the modal's notice ("Microbrand Atlas"). */
  sourceLabel: string;
}

/** Open the Create modal seeded with these values. */
export type CreateBrandSeed = BrandSeedBase;

/** Open the Edit modal for `brandId` with these values overlaid on the doc. */
export interface EditBrandPatchSeed extends BrandSeedBase {
  brandId: string;
}

/** Keys the Create modal has an input for. */
export const CREATE_FORM_KEYS = [
  'name',
  'displayName',
  'country',
  'city',
  'stateRegion',
  'assemblyLocation',
  'designLocation',
  'isShopifyBrand',
  'ecommercePlatform',
  'instagramUrl',
  'founder',
  'mainWebsite',
  'mainAddress',
  'founded',
  'isMicroBrand',
] as const satisfies readonly (keyof WatchBrand)[];

/** Keys the Edit modal has an input for. */
export const EDIT_FORM_KEYS = [
  ...CREATE_FORM_KEYS,
  'logo',
  'heroImage',
] as const satisfies readonly (keyof WatchBrand)[];

type FormKeys = readonly (keyof WatchBrand)[];

/** The seed values the modal has no input for, and so writes as-is. */
export function carriedSeedValues(
  values: Partial<WatchBrand>,
  formKeys: FormKeys,
): Partial<WatchBrand> {
  const owned = new Set<string>(formKeys);
  return Object.fromEntries(
    Object.entries(values).filter(([key, value]) => !owned.has(key) && value !== undefined),
  ) as Partial<WatchBrand>;
}

function sameValue(a: unknown, b: unknown): boolean {
  if (typeof a === 'string' && typeof b === 'string') return a.trim() === b.trim();
  return a === b;
}

/**
 * Did the value that was saved for a form-backed field come from the seed?
 * A reviewer who corrects a seeded country in the form has made it THEIR value,
 * and a provenance stamp naming the external source would then be a lie.
 *
 * Logos are compared on the source URL too: saving pulls an external logo into
 * our own Storage, so `logo` itself no longer equals what was seeded.
 */
function seededValueSurvived(
  path: string,
  seeded: Partial<WatchBrand>,
  saved: Partial<WatchBrand>,
): boolean {
  // Compared at the stamped path, not its top-level key: `founder.name` and
  // `founder.managed` are stamped separately, and correcting one of them must
  // not cost the other its stamp.
  const key = path;
  const seededValue = getPath(seeded, path);
  const savedValue = getPath(saved, path);
  if (key === 'logo') {
    return sameValue(savedValue, seededValue) || sameValue(saved.logoSourceUrl, seededValue);
  }
  return sameValue(savedValue, seededValue);
}

function getPath(obj: unknown, path: string): unknown {
  let current: unknown = obj;
  for (const part of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * The extra fields a seeded save writes on top of what the form produced.
 *
 * - carried values (the seed keys the modal has no input for);
 * - `externalRefs` and `fieldSources`, each merged over what the brand already
 *   holds, because `updateDoc` replaces a map wholesale;
 * - `fieldSources` entries only for fields whose saved value is still the
 *   seeded one.
 *
 * @param saved    what the form is about to write (form-backed keys)
 * @param existing the brand document being edited; omit when creating
 */
export function buildSeedWritePayload(params: {
  seed: BrandSeedBase;
  formKeys: FormKeys;
  saved: Partial<WatchBrand>;
  existing?: WatchBrand | null;
}): Partial<WatchBrand> {
  const { seed, formKeys, saved, existing } = params;
  const owned = new Set<string>(formKeys);
  const carried = carriedSeedValues(seed.values, formKeys);

  const fieldSources: BrandFieldSources = { ...existing?.fieldSources };
  for (const [path, stamp] of Object.entries(seed.fieldSources)) {
    const topLevelKey = path.split('.')[0];
    if (owned.has(topLevelKey) && !seededValueSurvived(path, seed.values, saved)) {
      continue;
    }
    fieldSources[path] = stamp;
  }

  return {
    ...carried,
    externalRefs: { ...existing?.externalRefs, ...seed.externalRefs },
    ...(Object.keys(fieldSources).length > 0 ? { fieldSources } : {}),
  };
}

const CARRIED_LABELS: Partial<Record<keyof WatchBrand, string>> = {
  location: 'Location',
  logo: 'Logo',
  heroImage: 'Hero image',
  isMicroBrand: 'Microbrand',
};

function displayCarried(key: string, value: unknown): string {
  if (key === 'location' && value && typeof value === 'object') {
    const { lat, lng } = value as { lat: number; lng: number };
    return `${lat}, ${lng}`;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/** The carried values as label/value pairs, for the modal's notice. */
export function describeCarriedValues(
  values: Partial<WatchBrand>,
  formKeys: FormKeys,
): { key: string; label: string; display: string }[] {
  return Object.entries(carriedSeedValues(values, formKeys)).map(([key, value]) => ({
    key,
    label: CARRIED_LABELS[key as keyof WatchBrand] ?? key,
    display: displayCarried(key, value),
  }));
}
